// =============================================================================
// SIREPPAR - Página de Detalle de Interacción
// =============================================================================

import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { interaccionService } from '../../services/api';
import { tipoTramiteLabel } from '../../utils/tiposExpediente';
import { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { puedeEscribirInteracciones } from '../../utils/permisosInteracciones';

// =============================================================================
// Constantes
// =============================================================================

const tipoLabels: Record<string, string> = {
  WHATSAPP:     'WhatsApp',
  LLAMADA:      'Llamada telefónica',
  EMAIL:        'Email',
  PRESENCIAL:   'Reunión presencial',
  VIDEOLLAMADA: 'Videollamada',
  NOTA_INTERNA: 'Nota interna',
};

const tipoColors: Record<string, string> = {
  WHATSAPP:     'bg-green-100 text-green-800',
  LLAMADA:      'bg-blue-100 text-blue-800',
  EMAIL:        'bg-yellow-100 text-yellow-800',
  PRESENCIAL:   'bg-purple-100 text-purple-800',
  VIDEOLLAMADA: 'bg-indigo-100 text-indigo-800',
  NOTA_INTERNA: 'bg-gray-100 text-gray-800',
};

const estadoColors: Record<string, string> = {
  PENDIENTE: 'bg-yellow-100 text-yellow-800',
  RESUELTO:  'bg-green-100 text-green-800',
};

const estadoLabels: Record<string, string> = {
  PENDIENTE: 'Pendiente de respuesta',
  RESUELTO:  'Resuelto',
};
const prioridadColors: Record<string, string> = {
  ALTA:  'bg-red-100 text-red-800',
  MEDIA: 'bg-yellow-100 text-yellow-800',
  BAJA:  'bg-green-100 text-green-800',
};

const prioridadLabels: Record<string, string> = {
  ALTA:  'Alta',
  MEDIA: 'Media',
  BAJA:  'Baja',
};
// =============================================================================
// Componente
// =============================================================================

export default function InteraccionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const puedeEscribir = puedeEscribirInteracciones(user?.rol);

  const { data, isLoading } = useQuery({
    queryKey: ['interaccion', id],
    queryFn: () => interaccionService.getById(id!),
  });

  const queryClient = useQueryClient();
  const [textoNota, setTextoNota] = useState('');
  const location = useLocation();
const notaRef = useRef<HTMLTextAreaElement>(null);

useEffect(() => {
  if (location.state?.focusNota && notaRef.current) {
    setTimeout(() => {
      notaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      notaRef.current?.focus();
    }, 300);
  }
}, []);

  const mutationNota = useMutation({
    mutationFn: (texto: string) => interaccionService.createNota(id!, texto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['interaccion', id] });
      setTextoNota('');
    },
  });

  const interaccion = data?.data;
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!interaccion) {
    return (
      <div className="text-center py-16 text-gray-500">
        <p>Interacción no encontrada.</p>
      </div>
    );
  }
  const handleAbrirAdjunto = async (path: string) => {
    const res = await interaccionService.getAdjuntoUrl(path);
    window.open(res.data.url, '_blank');
  };
  return (
    <div className="max-w-2xl mx-auto">
      {/* Encabezado */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/interacciones')}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            ← Volver
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Detalle de interacción</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => window.print()}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Imprimir
          </button>
          {puedeEscribir && (
          <button
            onClick={() => navigate(`/interacciones/${id}/editar`)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Editar
          </button>
          )}
        </div>
      </div>

      {/* Card de detalle */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">

        {/* Badges tipo + estado */}
        <div className="flex gap-2 mb-6">
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${tipoColors[interaccion.tipo] ?? 'bg-gray-100 text-gray-700'}`}>
            {tipoLabels[interaccion.tipo] ?? interaccion.tipo}
          </span>
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${estadoColors[interaccion.estado] ?? 'bg-gray-100 text-gray-700'}`}>
            {estadoLabels[interaccion.estado] ?? interaccion.estado}
          </span>
          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${prioridadColors[interaccion.prioridad] ?? 'bg-gray-100 text-gray-700'}`}>
            {prioridadLabels[interaccion.prioridad] ?? interaccion.prioridad}
          </span>
        </div>

        {/* Filas de datos */}
        <div className="space-y-3 text-sm">
          <div className="flex border-b border-gray-50 pb-3">
            <span className="text-gray-400 w-40 shrink-0">Fecha interacción</span>
            <span className="text-gray-900">
              {new Date(interaccion.fechaInteraccion).toLocaleDateString('es-AR')} a las{' '}
              {new Date(interaccion.fechaInteraccion).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} hs
            </span>
          </div>

          <div className="flex border-b border-gray-50 pb-3">
            <span className="text-gray-400 w-40 shrink-0">Partido</span>
            <span className="text-gray-900">{interaccion.partido?.denominacion ?? '-'}</span>
          </div>

          {interaccion.extrapartidaria && (
            <div className="flex border-b border-gray-50 pb-3">
              <span className="text-gray-400 w-40 shrink-0">Tipo</span>
              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
                Extrapartidaria
              </span>
            </div>
          )}

          {interaccion.entidad && (
            <div className="flex border-b border-gray-50 pb-3">
              <span className="text-gray-400 w-40 shrink-0">Entidad</span>
              <span className="text-gray-900">{interaccion.entidad}</span>
            </div>
          )}

          {interaccion.expediente && (
            <div className="flex border-b border-gray-50 pb-3">
              <span className="text-gray-400 w-40 shrink-0">Expediente</span>
              <span className="text-gray-900">{interaccion.expediente.numero}{interaccion.expediente.tipo ? ` — ${tipoTramiteLabel(interaccion.expediente.tipo)}` : ''}</span>
            </div>
          )}

          <div className="flex border-b border-gray-50 pb-3">
            <span className="text-gray-400 w-40 shrink-0">Nombre</span>
            <span className="text-gray-900">{interaccion.contactoNombre}</span>
          </div>

          {interaccion.contactoCargo && (
            <div className="flex border-b border-gray-50 pb-3">
              <span className="text-gray-400 w-40 shrink-0">Cargo</span>
              <span className="text-gray-900">{interaccion.contactoCargo}</span>
            </div>
          )}

          {interaccion.contactoTelefono && (
            <div className="flex border-b border-gray-50 pb-3">
              <span className="text-gray-400 w-40 shrink-0">Teléfono</span>
              <span className="text-gray-900">{interaccion.contactoTelefono}</span>
            </div>
          )}

          <div className="flex border-b border-gray-50 pb-3">
            <span className="text-gray-400 w-40 shrink-0">Resumen</span>
            <span className="text-gray-900">{interaccion.resumen}</span>
          </div>

          {interaccion.detalle && (
            <div className="flex flex-col gap-1 border-b border-gray-50 pb-3">
              <span className="text-gray-400">Notas y detalles</span>
              <span className="text-gray-900 whitespace-pre-line leading-relaxed">{interaccion.detalle}</span>
            </div>
          )}
          {interaccion.adjuntos && interaccion.adjuntos.length > 0 && (
            <div className="flex flex-col gap-2 border-b border-gray-50 pb-3">
              <span className="text-gray-400 text-sm">Archivos adjuntos</span>
              <div className="flex flex-col gap-1">
                {interaccion.adjuntos.map((path: string, i: number) => {
                  const filename = path.split('/').pop()?.replace(/^[^-]+-/, '') ?? path;
                  return (
                    <button
                      key={i}
                      onClick={() => handleAbrirAdjunto(path)}
                      className="text-left text-sm text-blue-600 hover:underline"
                    >
                      {filename}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

          {/* Notas de seguimiento */}
          <div className="mt-6 pt-4 border-t border-gray-100">
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Seguimiento</h2>

          {/* Lista de notas */}
          <div className="space-y-3 mb-4">
            {interaccion.notas?.length === 0 && (
              <p className="text-xs text-gray-400">Sin notas de seguimiento aún.</p>
            )}
            {interaccion.notas?.map((nota: any) => (
              <div key={nota.id} className="bg-gray-50 rounded-md px-3 py-2 text-sm">
                <span className="font-medium text-gray-700">
                  {nota.usuario.nombre} {nota.usuario.apellido}
                </span>
                <span className="text-gray-400 ml-2 text-xs">
                  {new Date(nota.creadoEn).toLocaleDateString('es-AR')} {new Date(nota.creadoEn).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} hs
                </span>
                <p className="text-gray-800 mt-1 whitespace-pre-line">{nota.texto}</p>
              </div>
            ))}
          </div>

          {/* Agregar nota */}
          {puedeEscribir && (
          <div className="flex flex-col gap-2">
            <textarea
              ref={notaRef}
              value={textoNota}
              onChange={e => setTextoNota(e.target.value)}
              placeholder="Agregar nota de seguimiento..."
              rows={2}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
            <div className="flex justify-end">
              <button
                onClick={() => mutationNota.mutate(textoNota)}
                disabled={!textoNota.trim() || mutationNota.isPending}
                className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50"
              >
                {mutationNota.isPending ? 'Guardando...' : 'Agregar nota'}
              </button>
            </div>
          </div>
          )}
        </div>

        {/* Auditoría */}
        <div className="mt-6 pt-4 border-t border-gray-100 text-xs text-gray-400">
          Creado por {interaccion.creador ? `${interaccion.creador.nombre} ${interaccion.creador.apellido}` : '—'} · {new Date(interaccion.creadoEn).toLocaleDateString('es-AR')} a las{' '}
          {new Date(interaccion.creadoEn).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })} hs
        </div>
      </div>
    </div>
  );
}
